import React, { memo, type ReactElement, useState } from 'react';
import {
  Badge,
  Box,
  Icon,
  InlineMessage,
  Link as DSLink,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { format } from 'date-fns';

import { type ViolationMitigatingControls } from '../../../features/Details/data-layer/detailsTypes';
import { CF_NEU_BLD, CF_SEC_BLD, testIds } from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import SodFieldSet from '../SodFieldSet';

import './SodMitigatingControls.css';

/**
 * Props interface for the SodMitigatingControls component
 */
interface SodMitigatingControlsProps {
  count?: number | null;
  headerText?: string;
  className?: string;
  handleLink: () => void;
  controls: ViolationMitigatingControls[];
  handleInlineAction: (control: ViolationMitigatingControls) => void;
}

/**
 * SodMitigatingControls Component - Displays and manages Segregation of Duties (SoD) mitigation controls
 *
 * @component
 * @example
 * const controls = [{
 *   id: 1,
 *   name: 'Control 1',
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31',
 *   expiryStatus: 0
 * }];
 *
 * return (
 *   <SodMitigatingControls
 *     count={1}
 *     headerText="Mitigating Control"
 *     controls={controls}
 *     handleLink={() => console.log('Link clicked')}
 *   />
 * )
 *
 * @param {SodMitigatingControlsProps} props - Component props
 * @param {number|null} [props.count=0] - Number of controls to display
 * @param {string} [props.headerText='Mitigating Control'] - Text to display in the header
 * @param {string} [props.className=null] - Additional CSS class names
 * @param {Function} props.handleLink - Callback function for handling link clicks
 * @param {ViolationMitigatingControls[]} props.controls - Array of control objects to display
 * @param {Function} [props.handleInlineAction] - Callback for inline action buttons
 *
 * @returns {ReactElement} Rendered component
 */
function SodMitigatingControls({
  count,
  headerText,
  className,
  controls,
  handleLink,
  handleInlineAction,
}: SodMitigatingControlsProps): ReactElement {
  const l = useInvertedLocalize();
  const wrapperClasses = cx('Sod-MitigatingControls', className);
  const {
    aag: {
      global: { globalDateFormat },
    },
  } = useTypedSelector((state) => state);

  const [inlineMessagesVisibility, setInlineMessagesVisibility] = useState<
    boolean[]
  >(Array(controls.length).fill(true));

  const handleInlineClose = (index: number) => {
    setInlineMessagesVisibility((prev) =>
      prev.map((status, idx) => {
        if (idx === index) {
          return !status;
        }

        return status;
      })
    );
  };

  // expiryStatus: 0 (Active), 1 (Expires soon), 2 (Expired)
  const isViewDetails = controls.some(
    (control) => control.expiryStatus === 1 || control.expiryStatus === 2
  );

  return (
    <Box
      tag='div'
      className={wrapperClasses}
      dataTestId={testIds.SOD_MITIGATING_CONTROLS}>
      <Box tag='div' className='Sod-MitigatingControls-header'>
        <Icon kind='MitigatingControls' color={CF_NEU_BLD} />
        <Typography kind='h4' color={CF_NEU_BLD}>
          {(() => (count ? `${headerText ?? ''}s` : headerText))()}
        </Typography>
        {count ? (
          <Badge
            kind='number'
            number={count}
            badgeStyle='outlined'
            color='neutral'
            size='medium'
          />
        ) : null}
      </Box>
      <Box tag='div' className='Sod-MitigatingControls-body'>
        {controls.map((control, index) => {
          const isFirst = Boolean(index === 0);
          const isLast = Boolean(index === controls.length - 1);

          const listItemClasses = cx(
            'Sod-MitigatingControls-bodyListItem',
            isFirst && 'Sod-MitigatingControls-bodyListItem--isFirst',
            isLast && 'Sod-MitigatingControls-bodyListItem--isLast'
          );

          return (
            <Box tag='div' className={listItemClasses} key={control.id}>
              <Typography kind='h4' color={CF_SEC_BLD}>
                {control.name}
              </Typography>
              <Box
                tag='div'
                className='Sod-MitigatingControls-bodyListItemFunctions'>
                <SodFieldSet
                  label={l('Start')}
                  value={format(new Date(control.startDate), globalDateFormat)}
                />
                <SodFieldSet
                  label={l('End')}
                  value={format(new Date(control.endDate), globalDateFormat)}
                />
              </Box>
              {control.expiryStatus === 1 && (
                <InlineMessage
                  kind='background'
                  text={l('Expires Soon.')}
                  colorTheme='warning'
                  size='small'
                  isVisible={inlineMessagesVisibility[index]}
                  onDismiss={() => handleInlineClose(index)}
                  isDismissible
                  leftIcon={<Icon kind='AlertWarningFilled' />}
                  linkComponent={
                    <DSLink
                      text={l('Extend')}
                      kind='button'
                      className='Sod-MitigatingControls-bodyListItemInlineLinks--isWarning'
                      onClick={() => handleInlineAction(control)}
                      suffixIconKind='ArrowRight'
                    />
                  }
                />
              )}
              {control.expiryStatus === 2 && (
                <InlineMessage
                  kind='background'
                  text={l('Expired.')}
                  colorTheme='critical'
                  size='small'
                  isVisible={inlineMessagesVisibility[index]}
                  onDismiss={() => handleInlineClose(index)}
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                  isDismissible
                  linkComponent={
                    <DSLink
                      text={l('Update')}
                      kind='button'
                      className='Sod-MitigatingControls-bodyListItemInlineLinks--isError'
                      onClick={() => handleInlineAction(control)}
                      suffixIconKind='ArrowRight'
                    />
                  }
                />
              )}
            </Box>
          );
        })}
      </Box>
      <DSLink
        text={l(!isViewDetails ? 'View Details' : 'View All')}
        color='brand'
        kind='button'
        onClick={handleLink}
        suffixIconKind='ArrowRight'
        size='medium'
        className='Sod-ViolationsSidePanelBody-links'
      />
    </Box>
  );
}

/**
 * Default props for the SodMitigatingControls component
 */
SodMitigatingControls.defaultProps = {
  count: 0,
  headerText: 'Mitigating Control',
  className: null,
};

export default memo(SodMitigatingControls);
